import {
  DotsVerticalIcon,
  DownloadIcon,
  ReceiptTaxIcon,
  VideoCameraIcon,
} from "@heroicons/react/solid";
import React, { Fragment, useState } from "react";
import dayjs from "dayjs";
import { getUrl } from "aws-amplify/storage";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";
import { classNames, extractS3Key } from "../../Utils";
import { ExclamationIcon, PhotographIcon } from "@heroicons/react/outline";
import { PencilIcon } from "@heroicons/react/solid";
import "./index.css";
import { useTranslation } from "react-i18next";
import { Button } from "../Button";
import { setEbillingInitialData, useEbillingDispatch } from "../../Context";
import { useNavigate } from "react-router-dom";

const downloadFile = async (key) => {
  const urlResult = await getUrl({
    path: ({ identityId }) => `private/${identityId}/${key}`,
    options: {
      useAccelerateEndpoint: true,
    },
  });
  const signedUrl = urlResult.url.href;
  window.open(signedUrl);
  try {
  } catch (error) {}
};

const ImageChat = ({ message, onLoad = () => {}, zoom = false }) => {
  const [loaded, setloaded] = useState(false);
  const [error, seterror] = useState(null);

  if (!zoom) {
    return (
      <div>
        <ExclamationIcon
          className={classNames(
            error ? "block" : "hidden",
            "h-12 w-12 text-gray-600",
          )}
        />
        <PhotographIcon
          className={classNames(
            loaded ? "hidden" : "block",
            "h-12 w-12 text-gray-600",
          )}
        />
        <img
          src={message.imageUrl}
          alt="message"
          className={classNames(
            loaded && !error ? "block" : "hidden",
            "max-h-24 rounded-lg",
          )}
          onError={(e) => {
            seterror(e);
            setloaded(true);
          }}
          onLoad={() => {
            setloaded(true);
            onLoad();
          }}
        />
      </div>
    );
  }

  return (
    <Zoom>
      <ExclamationIcon
        className={classNames(
          error ? "block" : "hidden",
          "h-12 w-12 text-gray-600",
        )}
      />
      <PhotographIcon
        className={classNames(
          loaded ? "hidden" : "block",
          "h-12 w-12 text-gray-600",
        )}
      />
      <img
        src={message.imageUrl}
        alt="message"
        className={classNames(
          loaded && !error ? "block" : "hidden",
          "max-h-24 rounded-lg",
        )}
        onError={(e) => {
          seterror(e);
          setloaded(true);
        }}
        onLoad={() => {
          setloaded(true);
          onLoad();
        }}
      />
    </Zoom>
  );
};

const FileChat = ({
  message,
  reply = false,
  theme = { primary: "#2563EB" },
}) => {
  return (
    <div className="flex items-center">
      {!reply && (
        <button
          onClick={() => {
            // Temporary function to fix uploaded path as key
            const key = extractS3Key(message.document.key);
            downloadFile(key);
          }}
          className="h-6 w-6 mr-2 rounded-full flex justify-center items-center shrink-0 bg-white"
        >
          <DownloadIcon className="w-4 h-4" style={{ color: theme.primary }} />
        </button>
      )}
      <span className="file-name">{message.document.key}</span>
    </div>
  );
};

const defaultTheme = {
  primary: "#2563EB",
  primaryText: "#FFFFFF",
};

export const ChatBubble = ({
  message,
  isAuthor,
  onReply = () => {},
  onEdit = () => {},
  withOptions = false,
  onClickRepliedMessage = () => {},
  className = "",
  onImageLoad = () => {},
  type = "",
  onJoinCall = undefined,
  theme = defaultTheme,
}) => {
  const { t } = useTranslation();
  const ebillingDispatch = useEbillingDispatch();
  const navigate = useNavigate();

  let duration = "";

  if (message.action?.params?.duration) {
    const time = message.action?.params?.duration;
    const minutes = Math.floor(time / 60);
    const seconds = time - minutes * 60;
    if (minutes === 0) {
      duration = `${seconds} ${t("seconds")}`;
    } else {
      duration = `${minutes} ${t("minutes")} ${seconds} ${t("seconds")}`;
    }
  }

  const isEditable =
    message.createdAt &&
    Date.now() - new Date(message.createdAt).getTime() <= 900000;

  const showEditButton =
    isEditable &&
    message.text &&
    isAuthor &&
    !message.system &&
    !message.action;

  const renderTextWithNewLines = (text) => {
    return text.split("\n").map((line, index) => (
      <React.Fragment key={index}>
        {line}
        <br />
      </React.Fragment>
    ));
  };

  return (
    <div
      className={classNames(
        className,
        isAuthor
          ? "rounded-l-lg rounded-br-lg"
          : "bg-slate-100 rounded-r-lg rounded-bl-lg",
        "group px-3 py-2 gap-2 flex flex-col justify-center max-w-[90%] sm:max-w-[70%]",
      )}
      style={isAuthor ? { backgroundColor: theme.primary } : {}}
    >
      <div className="flex items-center">
        {(type === "CALL" || type === "CALL_JOIN" || type === "CALL_LEAVE") && (
          <VideoCameraIcon
            className={classNames(
              isAuthor ? "text-white" : "text-slate-900",
              "w-8 h-8 mr-2",
            )}
          />
        )}
        {type === "EBILLING" && (
          <ReceiptTaxIcon
            className={classNames(
              isAuthor ? "text-white" : "text-slate-900",
              "w-8 h-8 mr-2",
            )}
          />
        )}
        <div
          className={classNames(
            isAuthor ? "text-white" : "text-black",
            "w-full flex flex-col text-sm",
          )}
        >
          {message.replyTo?.id && (
            <button
              onClick={() => {
                onClickRepliedMessage(message.replyTo.id);
              }}
            >
              <div className="p-2 mb-2 text-left rounded-lg bg-slate-900/10">
                {message.replyTo?.text && <p>{message.replyTo.text}</p>}
                {message.replyTo?.imageUrl && (
                  <ImageChat
                    onLoad={() => {
                      onImageLoad(`${message.id}-${message.replyTo.id}`);
                    }}
                    message={message.replyTo}
                  />
                )}
                {message.replyTo?.document && (
                  <FileChat reply message={message.replyTo} theme={theme} />
                )}
              </div>
            </button>
          )}
          {message.imageUrl && (
            <ImageChat
              onLoad={() => {
                onImageLoad(message.id);
              }}
              message={message}
              zoom
            />
          )}
          {message.document?.key && (
            <FileChat message={message} theme={theme} />
          )}
          {message.text && (
            <p className="overflow-hidden break-words">
              {renderTextWithNewLines(message.text)}
            </p>
          )}
          {message.action?.name === "ZOOM" && (
            <Button
              title="Join Zoom"
              className="mt-2"
              onClick={() => {
                if (
                  message.action.target === "URL" &&
                  message.action?.params?.id
                ) {
                  const newTab = window.open(
                    message.action.params.id,
                    "_blank",
                  );
                  newTab.focus();
                }
              }}
              outline
            />
          )}
          {(type === "CALL" || type === "CALL_LEAVE") &&
            message.action?.params?.duration && (
              <p
                className={classNames(
                  isAuthor ? "text-white" : "text-black",
                  "text-xs",
                )}
              >
                {duration}
              </p>
            )}
          {type === "CALL_JOIN" && !isAuthor && onJoinCall && (
            <Button
              title={t("Join")}
              className="mt-2"
              onClick={onJoinCall}
              outline
            />
          )}
          {message.action?.name === "EBILLING" && (
            <Button
              title="Create E-Billing"
              className="mt-2"
              onClick={() => {
                if (message.action?.params?.kap) {
                  setEbillingInitialData(ebillingDispatch, {
                    initialData: message.action?.params,
                  });
                  navigate(
                    `/ebilling/create?kap=${message.action?.params?.kap}${
                      message.action?.params?.kjs
                        ? `&kjs=${message.action.params.kjs}`
                        : ""
                    }`,
                  );
                }
              }}
              outline
            />
          )}
        </div>
      </div>
      <div
        className={classNames(
          isAuthor ? "text-white" : "text-gray-500",
          "flex justify-between items-center gap-2 text-xs",
        )}
      >
        <span>{dayjs(message.createdAt).format("HH:mm")}</span>
        <div className="relative flex flex-row-reverse items-center">
          {(message.image?.key || message.document?.key || message.text) &&
            withOptions && (
              <Popover className="inline-flex">
                {({ open }) => (
                  <>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <PopoverPanel
                        className={classNames(
                          isAuthor ? "right-0" : "left-0",
                          "-translate-y-full absolute -top-2 w-36 z-10 rounded-lg shadow-md border border-slate-100 bg-white focus:outline-none",
                        )}
                      >
                        {({ close }) => (
                          <div className="flex flex-col w-full text-slate-900">
                            <button
                              onClick={() => {
                                close();
                                onReply(message);
                              }}
                              className="relative flex flex-1 items-center m-1 p-1 rounded-lg hover:bg-slate-100"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                className="w-4 h-4"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3"
                                />
                              </svg>

                              <div className="ml-3 text-xs">{t("Reply")}</div>
                            </button>
                            {showEditButton && (
                              <button
                                onClick={() => {
                                  close();
                                  onEdit(message);
                                }}
                                className="relative flex flex-1 items-center m-1 p-1 rounded-lg hover:bg-slate-100"
                              >
                                <PencilIcon className="w-4 h-4" />
                                <div className="ml-3 text-xs">{t("Edit")}</div>
                              </button>
                            )}
                            {(message.image?.key || message.document?.key) && (
                              <button
                                onClick={() => {
                                  close();
                                  // Temporary function to fix uploaded path as key
                                  const key = extractS3Key(
                                    message.image?.key || message.document?.key,
                                  );
                                  downloadFile(key);
                                }}
                                className="relative flex flex-1 items-center m-1 p-1 rounded-lg hover:bg-slate-100"
                              >
                                <DownloadIcon className="w-4 h-4" />
                                <div className="ml-3 text-xs">
                                  {t("Download")}
                                </div>
                              </button>
                            )}
                          </div>
                        )}
                      </PopoverPanel>
                    </Transition>
                    <PopoverButton className="relative inline-flex items-center text-sm font-medium focus:z-10 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2">
                      <DotsVerticalIcon className="h-4 w-4 shrink-0" />
                    </PopoverButton>
                  </>
                )}
              </Popover>
            )}
          {(message.system || message.isEdited) && (
            <span className="italic text-xs">
              {message.system ? t("Auto-generated") : t("editedLabel")}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
